import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useCallback } from 'react';
import { Link } from 'react-router';
import {
  fade_in_down_keyframes,
  fade_in_fn,
  fade_in_left_keyframes,
  fade_in_right_keyframes
} from '../../../animations';
import { MainMenuItems } from '../../../MainMenuItems';
import { MenuItem } from '../../Menu/MenuItemWidget';
import { TagBadgeWidget } from '../../TagBadgeWidget';
import { LayoutWidget } from '../LayoutWidget';
import { AlgoliaSearchWidget } from './AlgoliaSearchWidget';
import { SignInLink, StartBuildingLink, StatusPageLink, ToggleThemeButton } from './SharedButtons';
import { PADDING } from '../CenterWidget';

export interface MobileDropdownMenuProps {
  willCollapse: boolean;
  collapse: () => void;
  toggleTheme: () => void;
  dark: boolean;
}

interface ActiveMenuItem {
  heading: string;
  subItems: MenuItem[];
  animationDirection: AnimationDirection;
  previous?: ActiveMenuItem;
}

enum AnimationDirection {
  LEFT = 'left',
  RIGHT = 'right',
  NONE = 'none'
}

export const MobileDropdownMenu: React.FC<MobileDropdownMenuProps> = (props: MobileDropdownMenuProps) => {
  const { willCollapse, toggleTheme, dark, collapse } = props;

  const [activeItem, setActiveItem] = React.useState<ActiveMenuItem>(() => ({
    previous: null,
    subItems: MainMenuItems,
    animationDirection: AnimationDirection.NONE,
    heading: 'Menu'
  }));

  const handleClick = useCallback((ev: React.MouseEvent, item: MenuItem) => {
    if (!(item.items?.length > 0)) {
      // Close the menu and use default link behavior
      collapse();
      return;
    }

    ev.preventDefault();
    setActiveItem((prev) => ({
      previous: prev,
      heading: item.label,
      subItems: item.items,
      animationDirection: AnimationDirection.RIGHT
    }));
  }, []);

  const onBackClick = useCallback(() => {
    if (!activeItem.previous) {
      return;
    }

    setActiveItem(() => ({ ...activeItem.previous, animationDirection: AnimationDirection.LEFT }));
  }, [activeItem]);
  const animDirection = willCollapse ? AnimationDirection.NONE : activeItem.animationDirection;

  const header = (
    <S.TopPanel animationDirection={animDirection} key={`${activeItem.heading}-${animDirection}`}>
      {!!activeItem.previous && (
        <S.BackButton onClick={onBackClick}>
          <S.BackAngle icon={faAngleLeft} />
          <S.Label>Back</S.Label>
        </S.BackButton>
      )}
      <S.Heading>{activeItem.heading}</S.Heading>
      <S.Search>
        <AlgoliaSearchWidget mobile onNavigate={collapse} />
      </S.Search>
    </S.TopPanel>
  );

  const footer = (
    <S.BottomPanel>
      <S.ButtonContainer>
        <StatusPageLink />
      </S.ButtonContainer>
      <S.ButtonContainer>
        <S.StartBuilding />
      </S.ButtonContainer>
      <S.ButtonContainer>
        <ToggleThemeButton toggleTheme={toggleTheme} dark={dark} />
      </S.ButtonContainer>
      <S.ButtonContainer>
        <S.SignIn />
      </S.ButtonContainer>
    </S.BottomPanel>
  );

  return (
    <S.MobileDropdownMenu collapsing={willCollapse} key={`MobileDropdownMenu-${willCollapse}`}>
      <LayoutWidget staticHeader={header} staticFooter={footer}>
        <S.MobileDropdownMenuItems animationDirection={animDirection} key={`${activeItem.heading}-${animDirection}`}>
          {activeItem.subItems.map((item, i) => (
            <MobileDropdownMenuItem key={`mobile-menu-item-${i}`} item={item} onClick={handleClick} />
          ))}
        </S.MobileDropdownMenuItems>
      </LayoutWidget>
    </S.MobileDropdownMenu>
  );
};

export interface MobileDropdownMenuItemProps {
  item: MenuItem;
  onClick?: (ev: React.MouseEvent, item: MenuItem) => void;
}

export const MobileDropdownMenuItem: React.FC<MobileDropdownMenuItemProps> = (props) => {
  const { item, onClick } = props;
  const hasSubItems = item.items?.length > 0;

  const handleClick = useCallback(
    (ev: React.MouseEvent) => {
      onClick(ev, item);
    },
    [item, onClick]
  );

  return (
    <S.MobileLink to={item.to} onClick={handleClick}>
      <S.Label>
        <span>{item.label}</span>
        {item.tag ? <TagBadgeWidget sinceVersion={item.tagVersion}>{item.tag}</TagBadgeWidget> : null}
      </S.Label>
      {hasSubItems && <S.Angle icon={faAngleRight} />}
      {item.icon ? <S.Icon icon={item.icon} /> : null}
    </S.MobileLink>
  );
};

namespace S {
  const fadeInDownAnim = (direction: 'normal' | 'reverse') => css`
    animation-name: ${fade_in_down_keyframes};
    animation-iteration-count: 1;
    animation-timing-function: ease-in;
    animation-duration: 0.25s;
    animation-direction: ${direction};
  `;

  const fadeInHorizontalAnim = (direction: AnimationDirection) =>
    direction === AnimationDirection.NONE
      ? null
      : css`
          animation-name: ${direction === AnimationDirection.LEFT ? fade_in_left_keyframes : fade_in_right_keyframes};
          animation-iteration-count: 1;
          animation-timing-function: ease-in;
          animation-duration: 0.25s;
        `;

  export const MobileDropdownMenu = styled.div<{ collapsing: boolean }>`
    label: MobileDropdownMenu;
    --mobile-menu-gutter: ${PADDING}px;
    background-color: ${(p) => p.theme.header.dropdownBackground};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 10px var(--mobile-menu-gutter);

    box-sizing: border-box;
    height: calc(100dvh - ${(p) => p.theme.header.height}px);

    opacity: ${(p) => (p.collapsing ? 0 : 1)};

    ${(p) => fadeInDownAnim(p.collapsing ? 'reverse' : 'normal')}
    overflow: hidden;

    display: flex;
    flex-direction: column;
  `;

  export const MobileDropdownMenuItems = styled.div<{ animationDirection: AnimationDirection }>`
    label: MobileDropdownMenuItems;
    display: flex;
    flex-direction: column;
    ${(p) => fadeInHorizontalAnim(p.animationDirection)}
  `;

  const MobileLinkCss = (theme: Theme) => css`
    text-decoration: none;
    min-height: ${theme.mobileDropdownMenu.height}px;
    color: ${theme.header.textColor};
    padding-left: 10px;
    padding-right: 10px;
    line-height: ${theme.mobileDropdownMenu.height}px;
    font-size: 14px;
    vertical-align: middle;

    display: flex;
    align-items: center;
    column-gap: 5px;
    user-select: none;
    opacity: 0.7;

    white-space: nowrap;
  `;

  export const MobileLink = styled(Link)`
    label: MobileLink;
    ${(p) => MobileLinkCss(p.theme)}
  `;

  export const StartBuilding = styled(StartBuildingLink)`
    label: GetStartedLink;
    margin: 8px 10px;
  `;

  export const SignIn = styled(SignInLink)`
    label: SignInLink;
    ${(p) => MobileLinkCss(p.theme)}
  `;

  export const Angle = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
    opacity: 0.5;
    user-select: none;
  `;

  export const BackAngle = styled(Angle)`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 20px;
    user-select: none;
  `;

  export const BackButton = styled.div`
    label: BackButton;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 10px;
    cursor: pointer;
    user-select: none;

    ${fade_in_fn('1s', 1)}
  `;

  export const TopPanel = styled.div<{ animationDirection: AnimationDirection }>`
    label: TopPanel;
    ${(p) => fadeInHorizontalAnim(p.animationDirection)}
    flex-grow: 1;
  `;

  export const Search = styled.div`
    margin-top: 12px;
    margin-bottom: 10px;
    margin-right: var(--mobile-menu-gutter);
  `;

  export const BottomPanel = styled.div`
    label: BottomPanel;
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    padding: 10px 0;
    width: 100%;
  `;

  export const ButtonContainer = styled.div`
    label: ButtonContainer;
    flex-grow: 1;

    display: flex;
    justify-content: center;
    align-items: center;

    border-left: 1px solid ${(p) => p.theme.header.dropdownBorder};
    &:first-of-type {
      border-left: none;
    }
  `;

  export const Heading = styled.h1`
    label: Heading;
    color: ${(p) => p.theme.header.textColor};
    margin-bottom: 10px;
    user-select: none;

    padding-left: 10px;
    padding-right: 10px;
  `;

  export const Label = styled.div`
    label: Label;
    flex-grow: 1;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  `;

  export const Icon = styled(FontAwesomeIcon)`
    padding-left: 10px;
    padding-right: 10px;
    opacity: 0.7;
    cursor: pointer;
  `;
}
